import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import TemplateSelector from './components/TemplateSelector';
import CredentialForm from './components/CredentialForm';
import BatchProcessor from './components/BatchProcessor';
import IssuanceReview from './components/IssuanceReview';
import IssuanceSuccess from './components/IssuanceSuccess';
import Icon from '../../components/AppIcon';

const CredentialIssuance = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [batchData, setBatchData] = useState([]);
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [issuanceResult, setIssuanceResult] = useState(null);

  const steps = [
    { id: 1, name: 'Template', description: 'Select credential template' },
    { id: 2, name: 'Configure', description: 'Fill credential details' },
    { id: 3, name: 'Review', description: 'Review and issue' },
    { id: 4, name: 'Complete', description: 'Issuance complete' }
  ];

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
  };

  const handleFormDataChange = (data) => {
    setFormData(data);
  };

  const handleBatchDataChange = (data) => {
    setBatchData(data);
  };

  const handleNextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, steps?.length));
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleToggleBatchMode = () => {
    setIsBatchMode(!isBatchMode);
    setBatchData([]);
    setFormData({});
  };

  const handleIssue = (result) => {
    setIssuanceResult(result);
    setCurrentStep(4);
  };

  const handleNewIssuance = () => {
    setCurrentStep(1);
    setSelectedTemplate(null);
    setFormData({});
    setBatchData([]);
    setIssuanceResult(null);
    setIsBatchMode(false);
  };

  const handleViewWallet = () => {
    navigate('/digital-wallet');
  };

  const handleViewDashboard = () => {
    navigate('/dashboard');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <TemplateSelector
            selectedTemplate={selectedTemplate}
            onTemplateSelect={handleTemplateSelect}
            onNext={handleNextStep}
          />
        );
      case 2:
        if (isBatchMode) {
          return (
            <BatchProcessor
              selectedTemplate={selectedTemplate}
              onBatchDataChange={handleBatchDataChange}
              onNext={handleNextStep}
              onBack={handlePrevStep}
            />
          );
        }
        return (
          <CredentialForm
            selectedTemplate={selectedTemplate}
            formData={formData}
            onFormDataChange={handleFormDataChange}
            onNext={handleNextStep}
            onBack={handlePrevStep}
          />
        );
      case 3:
        return (
          <IssuanceReview
            selectedTemplate={selectedTemplate}
            formData={formData}
            batchData={batchData}
            onIssue={handleIssue}
            onBack={handlePrevStep}
          />
        );
      case 4:
        return (
          <IssuanceSuccess
            issuanceResult={issuanceResult}
            onNewIssuance={handleNewIssuance}
            onViewWallet={handleViewWallet}
            onViewDashboard={handleViewDashboard}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Progress Steps */}
          {currentStep < 4 && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-2xl font-bold text-foreground">Issue Credentials</h1>
                
                {/* Batch Mode Toggle */}
                {currentStep === 1 && selectedTemplate && (
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">Single</span>
                    <button
                      onClick={handleToggleBatchMode}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        isBatchMode ? 'bg-primary' : 'bg-muted'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          isBatchMode ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className="text-sm text-muted-foreground">Batch</span>
                  </div>
                )}
              </div>
              
              <div className="flex items-center justify-between">
                {steps?.slice(0, 3)?.map((step, index) => (
                  <React.Fragment key={step?.id}>
                    <div className="flex items-center">
                      <div
                        className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                          currentStep >= step?.id
                            ? 'bg-primary border-primary text-primary-foreground'
                            : 'border-border text-muted-foreground'
                        }`}
                      >
                        {currentStep > step?.id ? (
                          <Icon name="Check" size={20} />
                        ) : (
                          <span className="text-sm font-medium">{step?.id}</span>
                        )}
                      </div>
                      <div className="ml-3 hidden sm:block">
                        <p className={`text-sm font-medium ${
                          currentStep >= step?.id ? 'text-foreground' : 'text-muted-foreground'
                        }`}>
                          {step?.name}
                        </p>
                        <p className="text-xs text-muted-foreground">{step?.description}</p>
                      </div>
                    </div>
                    {index < steps?.length - 2 && (
                      <div
                        className={`flex-1 h-0.5 mx-4 ${
                          currentStep > step?.id ? 'bg-primary' : 'bg-border'
                        }`}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* Step Content */}
          <div className="bg-card rounded-lg shadow-sm border border-border p-6">
            {renderStepContent()}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CredentialIssuance;